import { fabric } from "@utils/fabric";
import BaseHandler from "./BaseHandler";
import CoreHandler from "./CoreHandler";

const DEFAULT_GRID_SIZE = 20;
const GRID_COLOR = "#e0e0e0";

// Dynamic snap threshold algorithm
// Based on research of other grid systems (Adobe Illustrator, Figma, Sketch)
// Formula: base threshold + (grid size factor) + (zoom factor)
const BASE_SNAP_THRESHOLD = 8; // Base threshold in pixels
const GRID_SIZE_FACTOR = 0.15; // 15% of grid size
const ZOOM_FACTOR = 0.3; // Zoom compensation factor

class GridHandler extends BaseHandler {
  public coreHandler: CoreHandler;
  public isEnabled: boolean = false;
  private gridSize: number = DEFAULT_GRID_SIZE;
  private gridPositions: { x: number[]; y: number[] } = { x: [], y: [] };

  constructor(coreHandler: CoreHandler) {
    super();
    this.coreHandler = coreHandler;
  }

  initialize(): void {
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    if (!this.coreHandler.canvas) return;

    // Listen for zoom changes to update grid
    this.coreHandler.workspaceHandler.on(
      "zoom:changed",
      this.handleZoomChanged
    );

    // Listen for object moving to implement snapping
    this.coreHandler.canvas.on("object:moving", this.handleObjectMoving);

    // Listen for canvas render to draw grid
    this.coreHandler.canvas.on("after:render", this.drawGrid);

    // Listen for workspace changes
    this.coreHandler.eventHandler.on(
      "design:loaded",
      this.handleWorkspaceChanged
    );
  }

  private getWorkspaceBounds() {
    const workspace = this.coreHandler.workspaceHandler.getWorkspaceObject();
    if (!workspace) return null;

    // Use bounding rect which gives us the actual rendered position
    const boundingRect = workspace.getBoundingRect();

    // Return bounding rect directly - this is the actual rendered position
    return {
      left: boundingRect.left,
      top: boundingRect.top,
      width: boundingRect.width,
      height: boundingRect.height,
      right: boundingRect.left + boundingRect.width,
      bottom: boundingRect.top + boundingRect.height,
    };
  }

  private handleZoomChanged = (): void => {
    if (this.isEnabled) {
      this.calculateGridPositions();
      this.coreHandler.canvas?.renderAll();
    }
  };

  private handleWorkspaceChanged = (): void => {
    if (this.isEnabled) {
      this.calculateGridPositions();
      this.coreHandler.canvas?.renderAll();
    }
  };

  private handleObjectMoving = (e: fabric.IEvent): void => {
    if (!this.isEnabled || !e.target) return;

    const target = e.target;
    const bounds = this.getWorkspaceBounds();
    if (!bounds) return;

    const canvas = this.coreHandler.canvas;
    const zoom = canvas.getZoom();
    const vpt = canvas.viewportTransform;

    if (!vpt) return;

    // Get object dimensions in object coordinates
    const objectWidth = target.width! * target.scaleX!;
    const objectHeight = target.height! * target.scaleY!;
    const objectLeft = target.left!;
    const objectTop = target.top!;

    // Convert object coordinates to screen coordinates for snapping
    const screenLeft = objectLeft * vpt[0] + vpt[4];
    const screenTop = objectTop * vpt[3] + vpt[5];
    const screenWidth = objectWidth * vpt[0];
    const screenHeight = objectHeight * vpt[3];

    // Calculate all snap points in screen coordinates
    const snapPoints = {
      left: screenLeft,
      right: screenLeft + screenWidth,
      top: screenTop,
      bottom: screenTop + screenHeight,
      centerX: screenLeft + screenWidth / 2,
      centerY: screenTop + screenHeight / 2,
    };

    // Only snap if object center is within workspace
    if (
      snapPoints.centerX < bounds.left ||
      snapPoints.centerX > bounds.right ||
      snapPoints.centerY < bounds.top ||
      snapPoints.centerY > bounds.bottom
    ) {
      return;
    }

    const screenSnapThreshold = this.calculateDynamicSnapThreshold(zoom);
    let bestSnapX: { position: number; offset: number } | null = null;
    let bestSnapY: { position: number; offset: number } | null = null;
    let bestDistanceX = screenSnapThreshold;
    let bestDistanceY = screenSnapThreshold;

    // Find best X snap point (check left, center, right)
    for (const [key, pos] of Object.entries({
      left: snapPoints.left,
      center: snapPoints.centerX,
      right: snapPoints.right,
    })) {
      const nearestGrid = this.findNearestGridPosition(
        pos,
        this.gridPositions.x
      );
      const distance = Math.abs(pos - nearestGrid);

      if (distance < bestDistanceX) {
        bestDistanceX = distance;
        let offset = 0;

        switch (key) {
          case "left":
            offset = 0;
            break;
          case "center":
            offset = -screenWidth / 2;
            break;
          case "right":
            offset = -screenWidth;
            break;
        }

        bestSnapX = { position: nearestGrid, offset };
      }
    }

    // Find best Y snap point (check top, center, bottom)
    for (const [key, pos] of Object.entries({
      top: snapPoints.top,
      center: snapPoints.centerY,
      bottom: snapPoints.bottom,
    })) {
      const nearestGrid = this.findNearestGridPosition(
        pos,
        this.gridPositions.y
      );
      const distance = Math.abs(pos - nearestGrid);

      if (distance < bestDistanceY) {
        bestDistanceY = distance;
        let offset = 0;

        switch (key) {
          case "top":
            offset = 0;
            break;
          case "center":
            offset = -screenHeight / 2;
            break;
          case "bottom":
            offset = -screenHeight;
            break;
        }

        bestSnapY = { position: nearestGrid, offset };
      }
    }

    // Apply X snapping
    if (bestSnapX) {
      const newScreenLeft = bestSnapX.position + bestSnapX.offset;
      const newObjectLeft = (newScreenLeft - vpt[4]) / vpt[0];
      target.set({ left: newObjectLeft });
    }

    // Apply Y snapping
    if (bestSnapY) {
      const newScreenTop = bestSnapY.position + bestSnapY.offset;
      const newObjectTop = (newScreenTop - vpt[5]) / vpt[3];
      target.set({ top: newObjectTop });
    }
  };

  private findNearestGridPosition(
    position: number,
    gridPositions: number[]
  ): number {
    if (gridPositions.length === 0) {
      return position;
    }

    let nearest = gridPositions[0];
    let minDistance = Math.abs(position - nearest);

    for (const gridPos of gridPositions) {
      const distance = Math.abs(position - gridPos);
      if (distance < minDistance) {
        minDistance = distance;
        nearest = gridPos;
      }
    }

    return nearest;
  }

  private calculateGridPositions(): void {
    if (!this.coreHandler.canvas) return;

    const bounds = this.getWorkspaceBounds();
    if (!bounds) return;

    // Grid size in screen coordinates - need to scale with zoom
    const canvas = this.coreHandler.canvas;
    const zoom = canvas.getZoom();
    const gridSize = this.gridSize * zoom;

    // Calculate grid positions in screen coordinates
    this.gridPositions.x = [];
    this.gridPositions.y = [];

    // Calculate vertical line positions within workspace
    for (let i = 0; i <= bounds.width; i += gridSize) {
      this.gridPositions.x.push(bounds.left + i);
    }

    // Calculate horizontal line positions within workspace
    for (let i = 0; i <= bounds.height; i += gridSize) {
      this.gridPositions.y.push(bounds.top + i);
    }
  }

  private drawGrid = (): void => {
    if (!this.isEnabled || !this.coreHandler.canvas) return;

    const canvas = this.coreHandler.canvas;
    const bounds = this.getWorkspaceBounds();
    if (!bounds) return;

    const ctx = canvas.getContext();
    if (!ctx) return;

    // Save current context state
    ctx.save();

    // Set grid line style
    ctx.strokeStyle = GRID_COLOR;
    ctx.lineWidth = 1; // Use fixed line width since we're in screen coordinates
    ctx.globalAlpha = 0.5;

    // Clip to workspace boundaries in screen coordinates
    ctx.beginPath();
    ctx.rect(bounds.left, bounds.top, bounds.width, bounds.height);
    ctx.clip();

    // Draw vertical lines within workspace
    for (const x of this.gridPositions.x) {
      ctx.beginPath();
      ctx.moveTo(x, bounds.top);
      ctx.lineTo(x, bounds.bottom);
      ctx.stroke();
    }

    // Draw horizontal lines within workspace
    for (const y of this.gridPositions.y) {
      ctx.beginPath();
      ctx.moveTo(bounds.left, y);
      ctx.lineTo(bounds.right, y);
      ctx.stroke();
    }

    // Restore context state
    ctx.restore();
  };

  enable(): void {
    this.isEnabled = true;
    this.calculateGridPositions();
    this.coreHandler.canvas?.renderAll();
    this.emit("grid:changed", this.isEnabled);
  }

  disable(): void {
    this.isEnabled = false;
    this.coreHandler.canvas?.renderAll();
    this.emit("grid:changed", this.isEnabled);
  }

  private calculateDynamicSnapThreshold(zoom: number): number {
    // Dynamic threshold algorithm inspired by professional design tools
    // 1. Base threshold: consistent minimum snap area
    // 2. Grid size factor: larger grids need larger snap areas
    // 3. Zoom factor: maintain usability at different zoom levels

    const baseThreshold = BASE_SNAP_THRESHOLD;
    const gridSizeFactor = this.gridSize * GRID_SIZE_FACTOR;
    const zoomFactor = Math.max(1, 1 / zoom) * ZOOM_FACTOR;

    // Calculate final threshold
    const threshold = (baseThreshold + gridSizeFactor + zoomFactor) * zoom;

    // Clamp threshold between reasonable bounds
    const minThreshold = 5 * zoom;
    const maxThreshold = Math.min(this.gridSize * zoom * 0.4, 50 * zoom);

    return Math.max(minThreshold, Math.min(threshold, maxThreshold));
  }

  setGridSize(size: number): void {
    this.gridSize = Math.max(20, Math.min(100, size)); // Clamp between 20 and 100
    if (this.isEnabled) {
      this.calculateGridPositions();
      this.coreHandler.canvas?.renderAll();
    }
    this.emit("grid:size-changed", this.gridSize);
  }

  getGridSize(): number {
    return this.gridSize;
  }

  destroy(): void {
    this.coreHandler.workspaceHandler.off(
      "zoom:changed",
      this.handleZoomChanged
    );
    this.coreHandler.canvas?.off("object:moving", this.handleObjectMoving);
    this.coreHandler.canvas?.off("after:render", this.drawGrid);
    this.coreHandler.eventHandler.off(
      "design:loaded",
      this.handleWorkspaceChanged
    );
    this.disable();
  }
}

export default GridHandler;
